import localForage from 'localforage'
import {useCallback, useContext, useMemo, useState} from 'react'
import {defineMessages} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {Dispatch} from 'redux'
import {IStation} from '../../models/Station'
import {updateSession} from '../actions/session'
import {StaticStateContext} from '../components/containers/Context'
import {useAsyncEffect, useLocale, useUnmountedRef} from '../components/hocs/hooks'
import {Locale} from '../config/constants'
import {IState} from '../config/store'
import {II18nMessage} from '../reducers/i18n'
import {getGlobalEnv, isoFetch} from './coreUtil'
import {formatMessage} from './formatMessage'
import {notifyError} from './util'

const messages = defineMessages({
  cannotGetPublicViews: {
    id: 'session.cannotGetPublicViews',
    defaultMessage: 'Cannot get public views information'
  },
  cannotGetStations: {
    id: 'session.cannotGetStations',
    defaultMessage: 'Cannot get stations information'
  },
})
//return local storage value on the first load
//locale is prefixed into api endpoint
const makeCachedRequest = <T>(
  localStorageKey: string,
  version: string,
  sessionKey: 'stations' | 'publicViews',
  apiEndPoint: string,
  i18nErrorMsg: II18nMessage,
) => {
  let loaded: boolean
  return (hash: string) => async (dispatch: Dispatch, getState: () => IState) => {
    const CDN_BASE_URL = getGlobalEnv('CDN_BASE_URL')
    const {i18n: {locale}} = getState()
    let data
    try {
      const store = (await localForage.getItem<{value: T, version: string }>(localStorageKey)) || undefined
      if (store && store.version !== version) {
        await localForage.removeItem(localStorageKey)
      }
      data = store?.version && store.version === version ? store.value : undefined
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
        console.error(err) // tslint:disable-line:no-console
      }
    }
    if (loaded) return data
    loaded = true
    const fetchData = async () => {
      try {
        let localData = await isoFetch(`${CDN_BASE_URL}/${locale}${apiEndPoint}`, {
          qs: {hash},
          noCredentials: true
        })
        dispatch(updateSession({[sessionKey]: localData}))
        //ignore promise by purpose
        localForage.setItem(localStorageKey, {
          version,
          value: localData.map(pv => ({...pv, isLocal: true}))
        })
      } catch (err) {
        loaded = false
        notifyError(err.error || formatMessage(i18nErrorMsg))
        throw err
      }
    }
    if (data) setTimeout(fetchData)
    else fetchData()
    return data
  }
}
export const getPublicViews = makeCachedRequest(
  'public-views',
  '0.0.1',
  'publicViews',
  '/api/data/no-route/public-views',
  messages.cannotGetPublicViews,
)
export const getStations = makeCachedRequest(
  'stations',
  '0.0.1',
  'stations',
  '/api/data/no-route/stations',
  messages.cannotGetStations,
)
export const usePublicViews = () => {
  const [statePublicViews, setPublicViews] = useState([])
  const unmountedRef = useUnmountedRef()
  const storePublicViews = useSelector(({session: {publicViews}}: IState) => publicViews)
  const dispatch = useDispatch()
  const hash = useContext(StaticStateContext)!.hash
  useAsyncEffect(useCallback(async () => {
    if (!storePublicViews) {
      const publicViews = await dispatch(getPublicViews(hash))
      if (unmountedRef.current) return
      if (publicViews) setPublicViews(publicViews)
    }
  }, [hash, dispatch, storePublicViews, unmountedRef]))
  const locale = useLocale()
  return useMemo(() => (
      storePublicViews || statePublicViews || []
    ).filter(({showJa, showEn}) => locale === Locale.en && showEn
    || locale === Locale.ja && showJa
    ),
    [locale, statePublicViews, storePublicViews])
}
export const useStations = () => {
  const storeStations = useSelector(({session: {stations}}: IState) => stations)
  const [stateStations, setStations] = useState<ReadonlyArray<IStation>>([])
  const dispatch = useDispatch()
  const unmountedRef = useUnmountedRef()
  const hash = useContext(StaticStateContext)!.hash
  useAsyncEffect(useCallback(async () => {
    if (!storeStations) {
      const stations = await dispatch(getStations(hash))
      if (unmountedRef.current) return
      if (stations) setStations(stations)
    }
  }, [hash, storeStations, dispatch, unmountedRef]))
  const locale = useLocale()
  return useMemo(
    () => (
      storeStations || stateStations
    ).filter(
      ({approvedEn}) => locale === Locale.ja || approvedEn
    ), [storeStations, stateStations, locale])
}
